import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { DialogProps } from '../components/Dialog'
import type { StyleClasses } from '../styles'
import { Button, Grid, InputField, Typography, useDialogs } from '../'
import Dialog, { DialogTitle, DialogActions, DialogContent } from '../components/Dialog'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import ClearIcon from '@material-ui/icons/Clear'
import { isEmailValid } from '@paintscout/util'

const useStyles = makeStyles<Theme, SendEventConfirmationDialogProps>(
  (theme) => ({
    root: {},
    dialogTitle: {},
    dialogContent: {
      padding: theme.spacing(3),
      justifyContent: 'flex-start',
      display: 'flex'
    },
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2)
    },
    cancel: {},
    confirm: {},
    messageWrapper: {
      marginBottom: theme.spacing(3)
    }
  }),
  { name: 'SendEventConfirmationDialog' }
)

export interface SendEventConfirmationDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  title: string
  message: React.ReactElement
  missingEmailMessage?: React.ReactElement
  loading?: boolean
  onConfirm: (send: boolean, email?: string) => any
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
}

export default function SendEventConfirmationDialog(props: SendEventConfirmationDialogProps) {
  const classes = useStyles(props)
  const { onConfirm, onCancel, title, message, missingEmailMessage, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const [isLoading, setIsLoading] = useState(false)
  const { openDialog } = useDialogs()

  const rightContent = (
    <div onClick={onCancel} className={classes.closeIcon}>
      <ClearIcon />
    </div>
  )

  return (
    <Dialog {...baseDialogProps}>
      <DialogTitle classes={{ root: classes.dialogTitle }} rightContent={rightContent}>
        {title}
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>{message}</DialogContent>
      <DialogActions
        leftButton={
          <Button
            className={classes.confirm}
            onClick={() => handleSubmit(false)}
            variant={'text'}
            disabled={isLoading}
            data-testid="send-event-confirmation-dialog-confirm-button"
          >
            Don't Send
          </Button>
        }
      >
        <Button
          className={classes.confirm}
          onClick={() => handleSubmit(true)}
          variant={'contained'}
          disabled={isLoading}
          data-testid="send-event-confirmation-dialog-confirm-button"
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit(send: boolean) {
    if (!!missingEmailMessage && send) {
      openDialog(
        InputEmailDialog,
        {
          ...props,
          onSend: (email: string) => {
            onConfirm(true, email)
          },
          hideBackdrop: false
        },
        { replace: true }
      )
      return
    }
    setIsLoading(true)
    try {
      onConfirm(send)
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('error', { variant: 'error' })
    }
    setIsLoading(false)
  }
}

function InputEmailDialog(props: SendEventConfirmationDialogProps & { onSend: (email: string) => void }) {
  const classes = useStyles(props)
  const { onConfirm, onCancel, title, missingEmailMessage, onSend, ...baseDialogProps } = props
  const [email, setEmail] = useState('')
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  const rightContent = (
    <div onClick={onCancel} className={classes.closeIcon}>
      <ClearIcon />
    </div>
  )

  const handleSubmit = () => {
    if (!isEmailValid(email)) {
      setError(true)
      return
    }
    setLoading(true)
    onSend(email)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (error) {
      setError(false)
    }
  }

  return (
    <Dialog {...baseDialogProps}>
      <DialogTitle classes={{ root: classes.dialogTitle }} rightContent={rightContent}>
        <Typography variant="h2">{title}</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container justifyContent="flex-start" style={{ padding: '8px' }}>
          <Grid item xs={12} className={classes.messageWrapper}>
            {missingEmailMessage}
          </Grid>

          <Grid item xs={12}>
            <form id="send-event-confirmation-dialog-form" onSubmit={(e) => e.preventDefault()}>
              <InputField
                required
                label="Email"
                value={email}
                onChange={handleChange}
                fullWidth
                error={!!error}
                helperText={error ? 'Please enter a valid email address' : ''}
              />
            </form>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button className={classes.confirm} onClick={() => onConfirm(false)} variant={'text'} disabled={loading}>
            Don't Send
          </Button>
        }
      >
        <Button
          className={classes.confirm}
          variant={'contained'}
          onClick={handleSubmit}
          disabled={loading}
          type="submit"
          form="send-event-confirmation-dialog-form"
        >
          Add & Send
        </Button>
      </DialogActions>
    </Dialog>
  )
}
